import {
  Archive,
  ArrowLeft,
  ArrowRight,
  Ban,
  Book,
  BookOpen,
  Briefcase,
  Calendar,
  CalendarCheck,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CircleDot,
  CirclePlay,
  CircleUser,
  ClipboardList,
  Clock,
  Download,
  Ellipsis,
  Eye,
  EyeOff,
  FileText,
  Flag,
  Globe,
  GraduationCap,
  GripVertical,
  History,
  Image as ImageIcon,
  Inbox,
  Info,
  KeyRound,
  Library,
  Layers,
  LayoutDashboard,
  Lightbulb,
  List,
  LoaderCircle,
  Lock,
  LogOut,
  Mail,
  Copy,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Printer,
  RefreshCw,
  Search,
  Send,
  Settings,
  SquarePen,
  Trash2,
  TriangleAlert,
  User,
  UserPlus,
  Users,
  Video,
  X,
  type LucideIcon,
} from "lucide-react";
//
// The icon library may be imported ONLY in this file (same containment rule as sonner).

export type IconComponent = LucideIcon;

/**
 * The app's icon set, from Lucide (the set the Figma design system uses). Seeded minimally with
 * only what's needed now — add icons per feature as screens require them (no bulk glyph port).
 */
export const icons = {
  close: X,
  "chevron-down": ChevronDown,
  // Points at the reading-order end.
  "chevron-right": ChevronRight,
  "chevron-selector-vertical": ChevronsUpDown,
  check: Check,
  clipboard: ClipboardList, // the questions step's intro banner
  alert: CircleAlert,
  "alert-triangle": TriangleAlert,
  "info-circle": Info,
  bank: Library, // « Banque de questions » in the staff sidebar (Figma: books)
  key: KeyRound, // « Modifier » the password on « Mon compte »
  loading: LoaderCircle,
  eye: Eye,
  "eye-off": EyeOff,
  download: Download, // the dossier detail's Documents tab — open/download a document
  image: ImageIcon, // a document row whose file is an image
  video: Video, // a document row whose file is a video
  send: Send, // the "Demander" dialog's header icon
  waive: Ban, // the "Déroger" action/dialog
  resend: RefreshCw, // the "Renvoyer" action/dialog
  // app shell / navigation
  copy: Copy, // « Copier le lien »
  menu: Menu,
  "panel-left-close": PanelLeftClose, // sidebar — « Réduire le menu »
  "panel-left-open": PanelLeftOpen, // sidebar rail — « Développer le menu »
  "log-out": LogOut,
  user: User,
  "user-plus": UserPlus, // invite a staff member
  search: Search,
  // staff sidebar nav glyphs — one per screen, matching the Figma sidebar
  flag: Flag, // rounds
  "file-text": FileText, // a required-document row in the round builder
  inbox: Inbox, // applications
  "bar-chart": ChartColumn, // reporting
  briefcase: Briefcase, // the dossier detail's "Projet" card
  users: Users, // users
  "graduation-cap": GraduationCap, // « Cohortes » in the staff sidebar
  settings: Settings, // settings
  // round builder — list toolbar, wizard footer, question/document rows
  plus: Plus, // "New round" / "Add question" / "Add document"
  printer: Printer, // "Imprimer" — the dossier confirmation screen's own print action
  calendar: Calendar, // opening & closing datetime fields
  "calendar-check": CalendarCheck, // "Planifier l'ouverture" — the schedule confirm button
  trash: Trash2, // remove a question / document row
  "drag-handle": GripVertical, // question reorder grip
  "dots-horizontal": Ellipsis, // a card's "…" overflow menu trigger
  "arrow-left": ArrowLeft, // pagination — previous
  "arrow-right": ArrowRight, // pagination — next
  "book-closed": Book, // question bank
  // apply funnel — the round context panel's meta rows and document checklist
  clock: Clock, // "closes in N days" countdown row
  "circle-check": CircleCheck, // a document the applicant should prepare
  // competency library (Figma 04.1 / 04.2)
  library: BookOpen, // sidebar — « Bibliothèque de compétences »
  "book-open": BookOpen, // the competency-guidance panel in the question form
  layers: Layers, // « Enregistrer créera la version N » — the versioning notice
  "square-pen": SquarePen, // the bank row's edit action
  "layout-dashboard": LayoutDashboard, // sidebar — « Tableau de bord »
  "chevron-up": ChevronUp, // collapse the "rounds that used it" panel
  history: History, // "Version history" action
  list: List, // linked-question count badge
  lock: Lock, // a frozen round's pinned version · the undeletable-core note
  "circle-play": CirclePlay, // a round still running
  archive: Archive, // an archived round's meta line
  globe: Globe, // the round builder's timezone note
  // round builder step 4 — the sender identity fields
  mail: Mail,
  // round builder step 3 — the Porteur/Projet section bandeaus (Figma 748:31199/748:31210)
  "user-circle": CircleUser,
  lightbulb: Lightbulb,
  // The dossier preview's "not answered yet" bullet — the wizard's own vocabulary for an
  // unanswered question, deliberately NOT a check or a filled dot, which would claim something
  // had been done.
  "circle-dashed": CircleDashed,
  // The dossier question row's "En cours" marker (the question currently open, not yet answered)
  // — a solid dot, deliberately between the dashed "not reached" ring and the check "answered"
  // mark.
  "circle-dot": CircleDot,
  // The dossier question row's "Erreur" marker — distinct from `alert-triangle` (the non-blocking
  // eligibility warning) so a hard input error is never shape-confused with a warning that still
  // lets the applicant through.
  "circle-alert": CircleAlert,
} satisfies Record<string, IconComponent>;

export type IconKey = keyof typeof icons;
